import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, Grid, Stack } from '@mui/material'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import FormSelect from 'components/form-elements/FormSelect'
import { minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import {
  CreatePartnerCodeRequest,
  EPartnerCodeStatusOptions,
  EPartnerCodeType,
  EPartnerCodeTypeOptions,
  Nullish,
  PARTNER_CODE_LICENSE_DURATION_LABELS,
  PARTNER_FUNDED_FAMILY_SEATS,
  PartnerCode,
  PartnerCodeContactFields,
  UpdatePartnerCodeRequest,
} from 'types'
import { partnerCodeFormSettings, PartnerCodeFormType } from './PartnerCode.scheme'

interface Props {
  isOpen: boolean
  handleClose: () => void
  onGenerate: (request: CreatePartnerCodeRequest) => void
  onUpdate: (request: UpdatePartnerCodeRequest) => void
  loading: boolean
  editedItem: Nullish<PartnerCode>
}

// Backend rejects empty strings for optional contact fields — send null instead
const toNullableField = (value?: string) => {
  const trimmed = value?.trim()
  return trimmed?.length ? trimmed : null
}

const ModalContent = ({
  handleClose,
  onGenerate,
  onUpdate,
  loading,
  editedItem,
}: Omit<Props, 'isOpen'>) => {
  const isUpdateForm = !!editedItem

  const methods = useForm<PartnerCodeFormType>(partnerCodeFormSettings(editedItem))
  const { handleSubmit, watch } = methods

  // Only funded codes grant licenses: the count and the hint follow the type selector.
  const isFundedCode = watch('codeType') === EPartnerCodeType.funded

  const onSubmitHandler: SubmitHandler<PartnerCodeFormType> = (values) => {
    const contactFields: PartnerCodeContactFields = {
      contactFirstName: toNullableField(values.contactFirstName),
      contactLastName: toNullableField(values.contactLastName),
      contactEmail: toNullableField(values.contactEmail),
      contactPhone: toNullableField(values.contactPhone),
      note: toNullableField(values.note),
    }

    if (editedItem) {
      // Type, organization and license count are immutable — the backend accepts status only.
      onUpdate({ id: editedItem.id, status: values.status, ...contactFields })
    } else {
      onGenerate({
        codeType: values.codeType,
        organizationName: values.organizationName.trim(),
        // Unfunded codes have no user limit, so the count is sent for funded codes only.
        numberOfUsers: isFundedCode ? Number(values.numberOfUsers) : undefined,
        ...contactFields,
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormSelect
              label="Code Type"
              name="codeType"
              options={EPartnerCodeTypeOptions}
              required
              fullWidth
              disabled={isUpdateForm}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormInput
              label="Organization / Partner"
              name="organizationName"
              required
              fullWidth
              disabled={isUpdateForm}
            />
          </Grid>
          {isFundedCode && (
            <Grid item xs={12} sm={4}>
              <FormInput
                label="Number of Licenses"
                name="numberOfUsers"
                required
                fullWidth
                disabled={isUpdateForm}
              />
            </Grid>
          )}
          {isUpdateForm && (
            <Grid item xs={12} sm={4}>
              <FormSelect
                label="Status"
                name="status"
                options={EPartnerCodeStatusOptions}
                required
                fullWidth
              />
            </Grid>
          )}

          {isFundedCode && (
            <Grid item xs={12}>
              <Alert severity="info">
                Partner-Funded licenses are Annual Family Subscriptions for up to{' '}
                {PARTNER_FUNDED_FAMILY_SEATS} users. The fixed{' '}
                {PARTNER_CODE_LICENSE_DURATION_LABELS['1_year']} period starts individually when a
                license is activated.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <FormInput label="Name" name="contactFirstName" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput label="Last Name" name="contactLastName" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput label="Email" name="contactEmail" fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormPhone label="Phone" name="contactPhone" />
          </Grid>
          <Grid item xs={12}>
            <FormInput label="Note" name="note" fullWidth multiline minRows={3} />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={handleClose} sx={minButtonWidth}>
            Cancel
          </Button>
          <LoadingButton loading={loading} variant="contained" type="submit" sx={minButtonWidth}>
            {isUpdateForm ? 'Update' : 'Generate'}
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}

export const PartnerCodeGenerationModal = (props: Props) => {
  const { isOpen, handleClose, editedItem } = props

  return (
    <UIModal
      isOpen={isOpen}
      onClose={handleClose}
      width={640}
      title={editedItem ? `Update Partner Code ${editedItem.code}` : 'Partner Code Generation'}
    >
      <ModalContent {...props} />
    </UIModal>
  )
}
